import { ClearOutlined as ClearOutlinedIcon } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import is from '@sindresorhus/is';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useContext, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router';

import { route } from './constants';
import { JobPostings } from './JobPostings';
import { BaseSkillDescription, BaseSkillHeadline, JobPostingsRow, RelatedSkillsRow } from './Markups';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import {
    DefaultHardSoftProficiencyWithUnknownLevel,
    DefaultLanguageProficiencyWithUnknownLevel,
} from '../../../../../shared/source/data-contract/skill';
import {
    updateOnUserSkillsMutationKey,
    useUserHardSoftSkillsMutation,
    useUserHardSoftSkillsOptionsQuery,
    useUserHardSoftSkillsQuery,
} from '../../../../../shared/source/query-hooks/hard-soft-skills';
import {
    useUserLanguageSkillsMutation,
    useUserLanguageSkillsOptionsQuery,
    useUserLanguageSkillsQuery,
} from '../../../../../shared/source/query-hooks/language-skills';
import { InlineLoadingSpinner } from '../../../design/components/LoadingSpinner';
import { useSkillProficiencyLevelName } from '../../common/skills';
import { ComponentErrorBoundary, PageErrorBoundary, renderErrorCodePage } from '../../error-handling/ErrorBoundaries';
import { useSnackbars } from '../../error-handling/SnackBars';
import { useSkillDetailWithIsAmbitionQuery, useSkillRelatedJobsQuery } from '../../query-hooks/skill-details';
import { useAppDrawer } from '../app-drawers/AppDrawer';
import { route as onboardingRoute } from '../onboarding/constants';

import type {
    HardSoftSkillMutation,
    LanguageSkillMutation,
    SkillMutation,
    SkillMutationSource,
} from '../../../../../shared/source/data-contract/skill';
import type { SkillDetails } from '../../../../../shared/source/data-contract/skill-details';
import type { MutateOptions } from '@tanstack/react-query';
import type { FunctionComponent, ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';

const forbiddenSkillDetailsUrls = [onboardingRoute.patterns.onboarding];

export const useHandleSkillAction = (
    userId: string,
    skillId: string,
    source: SkillMutationSource,
): { readonly doesSkillExist: boolean; readonly handleSkillAction: (isLanguage: boolean) => void } => {
    const {
        applicationState: { customization },
    } = useContext(ApplicationContext);
    const usersConfiguration = customization.get('usersConfiguration').unwrapOr({
        userHardSoftDefaultLevel: DefaultHardSoftProficiencyWithUnknownLevel,
        userLanguageDefaultLevel: DefaultLanguageProficiencyWithUnknownLevel,
    });

    const queryClient = useQueryClient();
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();

    const { data: hardSoftSkillsOptions } = useUserHardSoftSkillsOptionsQuery(userId);
    const { data: languageSkillsOptions } = useUserLanguageSkillsOptionsQuery(userId);

    const replaceAllHardSoftSkillsMutation = useUserHardSoftSkillsMutation(userId, source);
    const replaceLanguageSkillsMutation = useUserLanguageSkillsMutation(userId, source);

    const { data: hardSoftSkillsData } = useUserHardSoftSkillsQuery({
        enabled: Boolean(hardSoftSkillsOptions?.canReadSkills),
        userId,
    });
    const { data: languageSkillsData } = useUserLanguageSkillsQuery({
        enabled: Boolean(languageSkillsOptions?.canReadLanguages),
        userId,
    });

    const doesHardSoftSkillExist = hardSoftSkillsData?.some(({ id }) => id === skillId) ?? false;
    const doesLanguageSkillExist = languageSkillsData?.some(({ id }) => id === skillId) ?? false;
    const doesSkillExist = doesHardSoftSkillExist || doesLanguageSkillExist;

    const handleSkillAction = (isLanguage: boolean): void => {
        const options: MutateOptions<void, unknown, readonly SkillMutation[]> = {
            onError: showErrorSnackbar,
            onSuccess: () => {
                showSuccessSnackbar(
                    doesSkillExist ? 'success.skills.removed-from-profile' : 'success.skills.added-to-profile',
                );
                queryClient.invalidateQueries({
                    predicate: (query) => query.queryKey.includes(updateOnUserSkillsMutationKey),
                });
            },
        };

        if (doesSkillExist) {
            if (doesHardSoftSkillExist) {
                const skillsToUpdate: readonly HardSoftSkillMutation[] = (hardSoftSkillsData ?? []).filter(
                    (sk) => sk.id !== skillId,
                );
                replaceAllHardSoftSkillsMutation.mutate(skillsToUpdate, options);
            }
            if (doesLanguageSkillExist) {
                const skillsToUpdate: readonly LanguageSkillMutation[] = (languageSkillsData ?? []).filter(
                    (sk) => sk.id !== skillId,
                );
                replaceLanguageSkillsMutation.mutate(skillsToUpdate, options);
            }
        } else {
            if (isLanguage) {
                const newSkill: LanguageSkillMutation = {
                    id: skillId,
                    level: usersConfiguration.userLanguageDefaultLevel,
                };
                const skillsToUpdate = [...(languageSkillsData ?? []), newSkill];
                replaceLanguageSkillsMutation.mutate(skillsToUpdate, options);
            } else {
                const newSkill: HardSoftSkillMutation = {
                    id: skillId,
                    level: usersConfiguration.userHardSoftDefaultLevel,
                };
                const skillsToUpdate = [...(hardSoftSkillsData ?? []), newSkill];
                replaceAllHardSoftSkillsMutation.mutate(skillsToUpdate, options);
            }
        }
    };

    return {
        doesSkillExist,
        handleSkillAction,
    };
};

const descriptionMaxChars = 255;
const SkillDescription: FunctionComponent<{ readonly skillDetails: SkillDetails }> = ({ skillDetails }) => {
    const { translate } = useContext(ApplicationContext);
    const { getProficiencyLevelName } = useSkillProficiencyLevelName();
    const [isCollapsed, setIsCollapsed] = useState(true);

    const handleCollapseButtonClick = (): void => setIsCollapsed((prev) => !prev);

    const proficiencyLevelsText = skillDetails.proficiencyLevelDetails
        ?.map(({ description, proficiencyLevel }) => {
            const levelName = getProficiencyLevelName(
                { level: proficiencyLevel, skillType: skillDetails.skillType },
                false,
            );

            return `${levelName} -> ${description}`;
        })
        ?.join('\\n\\n');

    const fullDescription = `${skillDetails.description}\\n\\n${proficiencyLevelsText}`;

    const isCollapsable = fullDescription.length > descriptionMaxChars;
    const truncatedText =
        isCollapsable && isCollapsed ? `${fullDescription.substring(0, descriptionMaxChars - 1)}…` : fullDescription;

    const contentDescription = useMemo(
        () =>
            truncatedText.split(/\\n/).map((nthContent, index) => {
                const nthKey = `content-${index}`;

                return (
                    <Fragment key={nthKey}>
                        {index > 0 ? <br /> : null}
                        {nthContent}
                    </Fragment>
                );
            }),
        [truncatedText],
    );

    const textContent = is.emptyArray(contentDescription)
        ? translate('pages.skill-details.messages.no-description')
        : contentDescription;

    return (
        <BaseSkillDescription
            collapsed={isCollapsed}
            content={textContent}
            isCollapsable={isCollapsable}
            onCollapseClick={handleCollapseButtonClick}
        />
    );
};

const RelatedJobs: FunctionComponent<{ readonly skillId: string }> = (props) => {
    const { skillId } = props;
    const skillRelatedJobsQuery = useSkillRelatedJobsQuery(skillId, {
        page: 0,
        size: 1,
    });

    return (
        <JobPostingsRow
            jobPostingsNode={<JobPostings skillId={skillId} />}
            totalJobPostings={skillRelatedJobsQuery.data?.pagination?.count ?? 0}
        />
    );
};

type SkillDetailsContentProps = {
    readonly isNavigationVisible?: boolean;
    readonly skillId: string;
};

export const SkillDetailsContent: FunctionComponent<SkillDetailsContentProps> = (props) => {
    const { isNavigationVisible, skillId } = props;

    const { actions, applicationState } = useContext(ApplicationContext);
    const {
        location: { pathname },
        myUserProfile,
    } = applicationState;
    const userId = myUserProfile.get('id').unwrapOr('');

    const { data: skillDetails, isPending } = useSkillDetailWithIsAmbitionQuery(userId, skillId);

    const { doesSkillExist, handleSkillAction } = useHandleSkillAction(userId, skillId, 'user');

    if (forbiddenSkillDetailsUrls.includes(pathname)) {
        return null;
    }

    if (isPending) {
        return <InlineLoadingSpinner />;
    }

    if (is.undefined(skillDetails)) {
        return null;
    }

    return (
        <Grid container={true} flexDirection="column" padding={3} rowGap={3}>
            <BaseSkillHeadline
                doesSkillExist={doesSkillExist}
                handleBack={() => actions.navigateBack()}
                handleSkillAction={handleSkillAction}
                isAmbition={skillDetails.isAmbition}
                showBackIcon={isNavigationVisible === true}
                skill={skillDetails}
                userId={userId}
            />
            <SkillDescription skillDetails={skillDetails} />
            <ComponentErrorBoundary>
                <RelatedSkillsRow skillId={skillId} />
            </ComponentErrorBoundary>
            <ComponentErrorBoundary>
                <RelatedJobs skillId={skillId} />
            </ComponentErrorBoundary>
        </Grid>
    );
};

export const SkillDetailsPage: FunctionComponent = () => {
    const pageUrlParams = useParams();
    const skillId = pageUrlParams[route.params.skillId];

    if (!is.nonEmptyString(skillId)) {
        return null;
    }

    return (
        <Container>
            <PageErrorBoundary>
                <Grid container={true} pb={4} pt={6}>
                    <SkillDetailsContent isNavigationVisible={true} skillId={skillId} />
                </Grid>
            </PageErrorBoundary>
        </Container>
    );
};

const SkillDetailsAppDrawerHeadline: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);
    const { palette } = useTheme();
    const { closeAppDrawer } = useAppDrawer();

    return (
        <Grid
            alignItems="center"
            container={true}
            justifyContent="space-between"
            sx={{ borderBottom: `solid 2px ${palette.cardborder.main}`, padding: '16px 24px' }}
        >
            <Typography alignItems="center" component="h5" display="flex" variant="h5">
                <span>{translate('pages.skill-details.headlines.title')}</span>
            </Typography>
            <Box>
                <IconButton sx={{ p: 0 }} onClick={closeAppDrawer}>
                    <ClearOutlinedIcon color="action" />
                </IconButton>
            </Box>
        </Grid>
    );
};

const SkillAppDrawerPermissionFallback = (props: FallbackProps): ReactNode => {
    const { translate } = useContext(ApplicationContext);
    const ErrorCodePage = renderErrorCodePage(translate);

    return (
        <Fragment>
            <SkillDetailsAppDrawerHeadline />
            <ErrorCodePage error={props.error} resetErrorBoundary={props.resetErrorBoundary} />
        </Fragment>
    );
};

export const SkillDetailsAppDrawerContent: FunctionComponent = () => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const skillId = applicationState.locationSearchParams.get('qDrawerSkillDetailsId');

    if (!is.nonEmptyString(skillId)) {
        return <Alert severity="error">{translate('pages.skill-details.messages.no-url-id')}</Alert>;
    }

    return (
        <ErrorBoundary fallbackRender={SkillAppDrawerPermissionFallback}>
            <SkillDetailsAppDrawerHeadline />
            <SkillDetailsContent isNavigationVisible={false} skillId={skillId} />
        </ErrorBoundary>
    );
};
